import { useEffect, useRef, useState } from "react";
import { Database, FileSpreadsheet, Route } from "lucide-react";
import timelineScreenshot from "@/assets/screenshots/smartlane-timeline-setup.png.asset.json";

type Model = {
  id: string;
  number: string;
  tag: string;
  title: string;
  accent: string;
  text: string;
  source: { label: string; file: boolean };
  inbound: string;
  smartlane: { label: string; ui: boolean };
  outbound: string;
  result: { label: string; file: boolean };
};

const MODELS: Model[] = [
  {
    id: "blackbox",
    number: "01",
    tag: "Blackbox, voll integriert",
    title: "Smartlane optimiert.",
    accent: "Sie bleiben im TMS.",
    text: "Planungsdaten werden per Schnittstelle übertragen, Smartlane plant und optimiert im Hintergrund. Der Tourenvorschlag wird via Schnittstelle an das TMS zurückgespielt, in dem anschließend die weitere Prüfung erfolgt.",
    source: { label: "TMS / ERP", file: false },
    inbound: "Planungsdaten · API",
    smartlane: { label: "Smartlane im Hintergrund", ui: false },
    outbound: "Tourenvorschlag · API",
    result: { label: "Zurück ins TMS", file: false },
  },
  {
    id: "hybrid-integriert",
    number: "02",
    tag: "Hybrid, voll integriert",
    title: "Automatisch optimieren.",
    accent: "Direkt anpassen.",
    text: "Die Planungsdaten werden via Schnittstelle übermittelt. Im hybriden Modell erfolgt die Prüfung und Bearbeitung des Tourenvorschlags in Smartlane. Die freigegebenen Touren werden anschließend wieder automatisiert an das TMS zurückgespielt.\u00a0",
    source: { label: "TMS / ERP", file: false },
    inbound: "Planungsdaten · API",
    smartlane: { label: "Smartlane Disposition", ui: true },
    outbound: "Tourenplanung · API",
    result: { label: "Zurück ins TMS", file: false },
  },
  {
    id: "hybrid-datei",
    number: "03",
    tag: "Hybrid, nicht integriert",
    title: "Ohne Schnittstelle.",
    accent: "Direkt starten.",
    text: "Sie exportieren Planungsdaten aus Ihrem TMS in einem beliebigen Dateiformat und laden sie hoch. Die fertige Tourenplanung geht als Datei zurück. So ist ein schneller Start auch ohne Schnittstellenintegration möglich.",
    source: { label: "TMS-Export (XLSX, CSV, JSON, XML)", file: true },
    inbound: "Planungsdaten · Upload",
    smartlane: { label: "Smartlane Disposition", ui: true },
    outbound: "Tourenplanung · Download",
    result: { label: "Import ins TMS", file: true },
  },
  {
    id: "stand-alone",
    number: "04",
    tag: "Stand-alone",
    title: "Tourenplanung.",
    accent: "Auch ohne TMS.",
    text: "Planungsdaten per Datei hochladen, Touren in Smartlane planen und bearbeiten und als Rollkarte oder Datei ausgeben – ganz ohne TMS oder ERP.",
    source: { label: "Planungsdaten (XLSX, CSV, JSON, XML)", file: true },
    inbound: "Upload",
    smartlane: { label: "Smartlane Stand-alone", ui: true },
    outbound: "Download",
    result: { label: "Rollkarte, XLSX, CSV, JSON/XML", file: true },
  },
];

export function UsageModelSwitcher({ onSelect }: { onSelect?: (id: string) => void }) {
  const [active, setActive] = useState(0);
  const m = MODELS[active];

  return (
    <div className="usage-models mt-8 min-w-0">
      <div role="tablist" aria-label="Nutzungsmodelle" className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        {MODELS.map((model, i) => {
          const selected = i === active;
          return (
            <button
              key={model.id}
              role="tab"
              aria-selected={selected}
              onClick={() => {
                setActive(i);
                onSelect?.(model.id);
              }}
              className={`min-w-0 rounded-xl border px-3 py-3 text-left transition-all duration-300 md:px-4 ${
                selected
                  ? "border-smartlane-orange bg-white shadow-[0_20px_50px_-30px_hsl(var(--smartlane-orange)/0.6)]"
                  : "border-rich-black/10 bg-white/60 hover:border-rich-black/25"
              }`}
            >
              <span className={`font-heading text-2xl ${selected ? "text-smartlane-orange" : "text-rich-black/35"}`}>
                {model.number}
              </span>
              <span className="mt-1 block font-body text-xs font-semibold uppercase tracking-[0.14em] text-rich-black">
                {model.tag}
              </span>
            </button>
          );
        })}
      </div>

      <div
        key={m.id}
        role="tabpanel"
        className="usage-model-panel relative mt-6 min-w-0 overflow-hidden rounded-2xl bg-rich-black p-4 text-white motion-safe:animate-fade-in md:p-6 min-[1240px]:px-3 min-[1240px]:py-8 min-[1280px]:px-5 min-[1441px]:p-10"
      >
        <div className="usage-model-layout grid min-w-0 gap-8">
          <div className="min-w-0">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.2em] text-smartlane-orange">
              {m.number} {m.tag}
            </p>
            <h3 className="font-heading mt-4 text-4xl uppercase leading-[0.95] tracking-wide text-white md:text-5xl">
              {m.title} <span className="block text-smartlane-orange">{m.accent}</span>
            </h3>
            <p className="copy mt-6 max-w-md text-base leading-relaxed text-white/80">{m.text}</p>
          </div>

          <div className="usage-model-diagram min-w-0">
          <div className="usage-model-flow grid min-w-0 items-center">
            <Node label={m.source.label} file={m.source.file} />
            <Connector label={m.inbound} />
            <div className="min-w-0 rounded-xl border border-white/15 bg-white/5 p-2">
              <div className="grid min-w-0 grid-cols-[auto_auto_minmax(0,1fr)] items-center gap-1.5 px-1 pb-2">
                <span className="h-2 w-2 shrink-0 rounded-full bg-smartlane-orange" />
                <span className="h-2 w-2 shrink-0 rounded-full bg-white/30" />
              <span className="ml-2 font-body text-xs uppercase leading-snug tracking-wider text-white/70">
                  {m.smartlane.label}
                </span>
              </div>
              <img
                src={timelineScreenshot.url}
                alt={m.smartlane.label}
                loading="lazy"
                className={`aspect-[16/10] w-full rounded-md object-cover object-left-top transition-all duration-500 ${
                  m.smartlane.ui ? "" : "opacity-30 grayscale blur-[1px]"
                }`}
              />
            </div>
            <Connector label={m.outbound} />
            <Node label={m.result.label} file={m.result.file} done />
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Node({ label, file, done }: { label: string; file: boolean; done?: boolean }) {
  const Icon = done ? Route : file ? FileSpreadsheet : Database;
  return (
    <div className="usage-model-node flex min-w-0 flex-col items-center gap-2 rounded-xl border border-white/15 bg-white/[0.04] px-3 py-4 text-center min-[1240px]:py-5">
      <Icon className={`h-6 w-6 shrink-0 ${done ? "text-smartlane-orange" : "text-white/80"}`} />
      <span className="font-body text-base font-semibold leading-snug text-white [overflow-wrap:break-word]">
        {label}
      </span>
    </div>
  );
}

const LINE_COLOR = "rgba(226, 232, 240, 0.62)";
const DOT_COLOR = "rgba(226, 232, 240, 0.85)";
const TIP_COLOR = "#F15A24";
const DASH = "0.1 6";
const STROKE = 2;
const END_R = 2.75;

function useSize() {
  const ref = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setSize({ w: el.offsetWidth, h: el.offsetHeight });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return { ref, w: size.w, h: size.h };
}

function Connector({ label }: { label: string }) {
  return (
    <div className="relative min-w-0">
      <div className="usage-wire-vertical relative h-20">
        <Wire label={label} vertical />
      </div>
      <div className="usage-wire-horizontal relative h-14">
        <Wire label={label} />
      </div>
    </div>
  );
}

function ConnectorLabel({ label, vertical }: { label: string; vertical?: boolean }) {
  const [main = "", sub = ""] = label.split("·");
  return (
    <span className={`absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center gap-x-1.5 ${vertical ? "whitespace-nowrap" : "w-max max-w-[calc(100%-12px)] flex-wrap gap-y-1 text-center"} rounded-md border border-white/30 bg-rich-black px-2 py-1 font-body text-[11px] font-semibold uppercase leading-snug tracking-[0.03em] text-white`}>
      <span>{main.trim()}</span>
      {sub ? <span className="text-white/65">· {sub.trim()}</span> : null}
    </span>
  );
}

/** Gepunktete Linie, neutrale Endpunkte, genau ein orangefarbener Richtungsmarker (Flussrichtung links→rechts bzw. oben→unten). */
function Wire({ label, vertical }: { label: string; vertical?: boolean }) {
  const { ref, w, h } = useSize();
  const len = vertical ? h : w;
  const c = vertical ? w / 2 : h / 2;
  const a = END_R;
  const b = Math.max(a + 1, len - END_R);
  // Marker sits between label and end point.
  const tip = b - 8;
  const pt = (along: number, across: number) => (vertical ? `${c + across} ${along}` : `${along} ${c + across}`);
  return (
    <div ref={ref} className="relative h-full w-full">
      {w > 0 && h > 0 && (
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="absolute inset-0 overflow-visible" aria-hidden="true">
          <path
            d={`M ${pt(a, 0)} L ${pt(b, 0)}`}
            stroke={LINE_COLOR}
            strokeWidth={STROKE}
            strokeLinecap="round"
            strokeDasharray={DASH}
            fill="none"
          />
          <circle cx={vertical ? c : a} cy={vertical ? a : c} r={END_R} fill={DOT_COLOR} />
          <circle cx={vertical ? c : b} cy={vertical ? b : c} r={END_R} fill={DOT_COLOR} />
          <path
            d={`M ${pt(tip - 4, -4)} L ${pt(tip + 1, 0)} L ${pt(tip - 4, 4)}`}
            fill="none"
            stroke={TIP_COLOR}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      <ConnectorLabel label={label} vertical={vertical} />
    </div>
  );
}
